import BadgeOutlined from '@mui/icons-material/BadgeOutlined'
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined'
import PersonOutlined from '@mui/icons-material/PersonOutlined'
import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material'
import useUsersById from '@/hooks/useUsersById'
import { useGetPositions } from '@/service-library/hooks/positions'
import { WorkflowStateAction } from '@/types/workflow-state-actions'

type SendNotificationDisplayProps = {
  action: WorkflowStateAction
}

export default function SendNotificationDisplay({
  action,
}: SendNotificationDisplayProps) {
  const { title, body } = action.params
  const userIds: string[] = action.params.user_ids
  const positionIds: string[] = action.params.position_ids

  const { usersById, isPending: usersPending } = useUsersById({ userIds })

  const { positions, isPending: positionsPending } = useGetPositions({
    filters: {
      id__in: positionIds.join(),
      limit: `${positionIds.length}`,
      fields__only: 'id,full_name',
    },
    enabled: positionIds.length > 0,
  })
  const positionsLoading = positionIds.length > 0 && positionsPending

  const recipients = [
    ...userIds.flatMap((id) => {
      const user = usersById.get(id)
      return user ? [{ id, label: user.name, icon: <PersonOutlined /> }] : []
    }),
    ...positions.map(({ id, full_name }) => ({
      id,
      label: full_name,
      icon: <BadgeOutlined />,
    })),
  ]

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ bgcolor: 'action.hover', borderRadius: 2, py: 1.5, px: 1.75 }}
      >
        <NotificationsOutlined sx={{ color: 'text.secondary' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="flex-start" useFlexGap>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: '24px' }}
        >
          To
        </Typography>
        {usersPending || positionsLoading ? (
          <Skeleton variant="rounded" width={120} height={24} />
        ) : (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ flex: 1, minWidth: 0 }}
          >
            {recipients.map(({ id, label, icon }) => (
              <Chip
                key={id}
                size="small"
                icon={icon}
                label={label}
                sx={{
                  height: 'auto',
                  minHeight: 24,
                  maxWidth: '100%',
                  '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 },
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {positionIds.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Only positions that apply to the document&apos;s company will be
          notified.
        </Typography>
      )}
    </Stack>
  )
}
